import { LoadPanel, ScrollView } from "devextreme-react";
import { Button } from "devextreme-react/button";
import {
  Column,
  DataGrid,
  Button as DataGridButton,
  DataGridRef,
  FilterRow,
  Item,
  Toolbar,
} from "devextreme-react/data-grid";
import { ISelectBoxOptions } from "devextreme-react/select-box";
import { confirm } from "devextreme/ui/dialog";
import notify from "../../lib/notify";
import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  deleteDocumentSendRequest,
  getDocumentSendRequest,
} from "../../api/document-send-requests";
import DocumentSendRequestCreatePopup from "../../components/document-send-request-create-popup/DocumentSendRequestCreatePopup";
import DocumentSendRequestRealizePopup from "../../components/document-send-request-realize-popup/DocumentSendRequestRealizePopup";
import { useAuth } from "../../contexts/auth";
import { DocumentSendRequestWithNames, RoleEnum } from "../../types";

/*
Do zrobienia:
- ograniczenie liczby pobieranych rekordów poprzez dodanie pól do wyboru roku i miesiąca

1) Pracownik
- widzi tylko swoje prośby (podział na zrealizowane i w realizacji?)
- ma możliwość utworzenia nowej
- ma możliwość usunięcia przed zatwierdzeniem
2) Majster
- widzi tylko swoje prośby
- ma możliwość utworzenia nowej
- ma możliwość usunięcia przed zatwierdzeniem
3) Manager
- widzi tylko swoje prośby
- ma możliwość utworzenia nowej
- ma możliwość usunięcia przed zatwierdzeniem
4) Kierownik
- widzi tylko swoje prośby
- ma możliwość utworzenia nowej
- ma możliwość usunięcia przed zatwierdzeniem
5) KadrySpolki
- widzi tylko prośby ze swojej spółki (podział na zrealizowane i do zrealizowania)?
- nie ma możliwości tworzenia próśb
- ma możliwość realizacji próśb
6) AdministratorSpolki
- brak dostępu
7) AdministratorGlowny
- brak dostępu
*/
export const Documents = () => {
  const { t, i18n } = useTranslation();
  const { user } = useAuth();
  const dataGridRef = useRef<DataGridRef>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [documentSendRequests, setDocumentSendRequests] = useState<
    DocumentSendRequestWithNames[]
  >([]);
  const [
    showDocumentSendRequestCreatePopup,
    setShowDocumentSendRequestCreatePopup,
  ] = useState(false);
  const [documentSendRequestToRealize, setDocumentSendRequestToRealize] =
    useState<DocumentSendRequestWithNames>();
  const [mode, setMode] = useState(0);

  const isCompanyStaff = user?.roles?.includes(RoleEnum.KadrySpolki) ?? false;

  const fetchDocumentSendRequest = useCallback(() => {
    setIsLoading(true);
    getDocumentSendRequest(mode)
      .then(setDocumentSendRequests)
      .catch((error) => notify(error.message, "error", 5000))
      .finally(() => setIsLoading(false));
  }, [mode]);

  const handleDeleteDocumentSendRequest = useCallback(
    (id: number) => {
      setIsLoading(true);
      deleteDocumentSendRequest(id)
        .then(() => {
          notify(
            t("documentSendRequests.deletePopup.onDeleted"),
            "success",
            5000
          );
          fetchDocumentSendRequest();
        })
        .catch((error) => notify(error.message, "error", 5000))
        .finally(() => setIsLoading(false));
    },
    [fetchDocumentSendRequest, t]
  );

  useEffect(() => {
    fetchDocumentSendRequest();
  }, [fetchDocumentSendRequest, mode, i18n.language]);

  return (
    <div>
      {isLoading ? (
        <LoadPanel
          visible
          showIndicator
          showPane
          shading
          shadingColor="rgba(0, 0, 0, 0.3)"
        />
      ) : null}
      {showDocumentSendRequestCreatePopup ? (
        <DocumentSendRequestCreatePopup
          handleCreated={() => {
            setShowDocumentSendRequestCreatePopup(false);
            fetchDocumentSendRequest();
            notify(
              t("documentSendRequests.createPopup.onCreatedMessage"),
              "success",
              5000
            );
          }}
          handleClose={() => setShowDocumentSendRequestCreatePopup(false)}
        />
      ) : null}
      {documentSendRequestToRealize ? (
        <DocumentSendRequestRealizePopup
          documentSendRequest={documentSendRequestToRealize}
          handleCompleted={() => {
            setDocumentSendRequestToRealize(undefined);
            fetchDocumentSendRequest();
            notify(
              t("documentSendRequests.realizePopup.onCompleteMessage"),
              "success",
              5000
            );
          }}
          handleClose={() => setDocumentSendRequestToRealize(undefined)}
        />
      ) : null}
      <ScrollView useNative>
        <div className="p-6">
          <div className="flex flex-col md:flex-row justify-between gap-4">
            <div>
              <div className="mb-2 text-2xl font-bold leading-none">
                {t("documentSendRequests.header")}
              </div>
              <div className="text-base">
                {t("documentSendRequests.description")}
              </div>
            </div>
            {!isCompanyStaff ? (
              <Button
                className="flex-shrink-0"
                text={t("documentSendRequests.createButton")}
                onClick={() => setShowDocumentSendRequestCreatePopup(true)}
              />
            ) : null}
          </div>
          <DataGrid
            ref={dataGridRef}
            dataSource={documentSendRequests}
            remoteOperations={{
              paging: true,
            }}
            showBorders
            allowColumnResizing
            allowColumnReordering
            searchPanel={{
              visible: true,
              placeholder: t("searchPlaceholder"),
            }}
            paging={{
              pageSize: 5,
            }}
            pager={{
              visible: true,
              showInfo: true,
              showNavigationButtons: true,
            }}
          >
            <Toolbar>
              <Item
                name="searchPanel"
                location="before"
                locateInMenu="auto"
                cssClass="datagrid-search-panel-without-margin-left"
              />
              <Item
                location="after"
                locateInMenu="auto"
                widget="dxSelectBox"
                options={
                  {
                    dataSource: [
                      {
                        value: 0,
                        label: t(
                          "documentSendRequests.viewMode.options.notRealized"
                        ),
                      },
                      {
                        value: 1,
                        label: t(
                          "documentSendRequests.viewMode.options.realized"
                        ),
                      },
                    ],
                    valueExpr: "value",
                    displayExpr: "label",
                    label: t("documentSendRequests.viewMode.label"),
                    labelMode: "floating",
                    value: mode,
                    onValueChanged: (e) => setMode(e.value),
                    width: 175,
                  } as ISelectBoxOptions
                }
              />
            </Toolbar>
            <FilterRow visible />
            <Column
              dataField="applicant"
              dataType="string"
              caption={t("documentSendRequests.dataGrid.applicant")}
              minWidth={200}
            />
            <Column
              dataField="applicantEmailAddress"
              dataType="string"
              caption={t("documentSendRequests.dataGrid.emailAddress")}
              minWidth={200}
            />
            <Column
              dataField="documentTypeWithDate"
              dataType="string"
              caption={t("documentSendRequests.dataGrid.documentTypeWithDate")}
              minWidth={200}
            />
            <Column
              dataField="dateCreated"
              dataType="datetime"
              format="dd.MM.yyyy, HH:mm:ss"
              caption={t("documentSendRequests.dataGrid.dateCreated")}
              minWidth={175}
            />
            <Column
              dataField="attendant"
              dataType="string"
              caption={t("documentSendRequests.dataGrid.attendant")}
              minWidth={250}
            />
            <Column
              dataField="dateOfCompletion"
              dataType="datetime"
              format="dd.MM.yyyy, HH:mm:ss"
              caption={t("documentSendRequests.dataGrid.dateOfCompletion")}
              minWidth={175}
            />
            <Column
              dataField="attendantComment"
              dataType="string"
              caption={t("documentSendRequests.dataGrid.attendantComment")}
              minWidth={200}
            />
            <Column type="buttons" alignment="right">
              <DataGridButton
                icon="message"
                text={t("documentSendRequests.dataGrid.buttons.realize")}
                hint={t("documentSendRequests.dataGrid.buttons.realize")}
                visible={({ row }) =>
                  isCompanyStaff && row.data.dateOfCompletion == null
                }
                onClick={({ row }) =>
                  setDocumentSendRequestToRealize(row?.data)
                }
              />
              <DataGridButton
                icon="trash"
                text={t("documentSendRequests.dataGrid.buttons.delete")}
                hint={t("documentSendRequests.dataGrid.buttons.delete")}
                visible={({ row }) =>
                  !isCompanyStaff && row.data.dateOfCompletion === null
                }
                onClick={({ row }) => {
                  confirm(
                    t("documentSendRequests.deletePopup.message"),
                    t("documentSendRequests.deletePopup.title")
                  ).then((decision) => {
                    if (decision) {
                      if (row) {
                        const documentSendRequestId = row.data.id;
                        handleDeleteDocumentSendRequest(documentSendRequestId);
                      }
                    }
                  });
                }}
              />
            </Column>
          </DataGrid>
        </div>
      </ScrollView>
    </div>
  );
};
